'use client';
import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';

import { ProfileRepeatOrderButton } from '@eliadmin/core/src/components/profile/profile-repeat-order-button';

import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type {
  CustomerOrder,
  OrderDocument,
} from '@eliadmin/core/src/api/customers/customers-types';

import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { ProfileOrderStatus } from './profile-order-status';
import { downloadFileFromBlob } from '@eliadmin/core/src/api/general/general-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

interface ProfileOrderDialogContentProps {
  order: CustomerOrder;
}

export function ProfileOrderDialogContent(props: ProfileOrderDialogContentProps) {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const me = useCustomerStore(s => s.me);

  const isPaid = !!props.order.paid;

  const handleDownload = async (document: OrderDocument) => {
    if (!me?.authToken) return;
    downloadFileFromBlob({
      fileId: document.id,
      authToken: me.authToken,
      locale,
      fileName: document.name + '_' + document.number,
    });
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-start gap-6',
        'ea-profile-order-dialog__content',
      )}
    >
      <div className="flex max-h-[70vh] flex-col items-start justify-start gap-6 overflow-y-auto">
        {props.order.items.map((item, index) => {
          return (
            <>
              <div key={item.id} className="flex w-full items-center justify-between gap-4">
                <Image
                  src={item.image?.url}
                  alt={resolveImageAlt(item?.image, item?.name)}
                  width={0}
                  height={0}
                  sizes="50vw"
                  className={cn(
                    'max-h-[72px] min-h-[72px] min-w-[72px] max-w-[72px] overflow-hidden rounded-[16px] bg-white object-contain md:max-h-[100px] md:min-h-[100px] md:min-w-[100px] md:max-w-[100px]',
                  )}
                />

                <div className="flex w-full flex-col items-start justify-start gap-2">
                  <div className="flex w-full flex-col items-start justify-start gap-1">
                    <Heading title={item.name} tag="h5" className={{ title: 'uppercase' }} />
                  </div>
                  <div className="flex w-full items-center justify-between">
                    <p className={cn('font-semibold text-black', 'h5')}>
                      {item.quantity} x {item.price.toFixed(2)} {currency}
                    </p>
                    <p className={cn('font-semibold text-black', 'h5')}>
                      {(item.quantity * item.price).toFixed(2)} {currency}
                    </p>
                  </div>
                </div>
              </div>
              {index < props.order.items.length - 1 && <Separator />}
            </>
          );
        })}
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-2 rounded-[4px] bg-[#FAF6E5] p-4 sm:p-5 lg:p-6">
        <div className="grid w-full grid-cols-2 gap-6">
          <Paragraph>{t('text394')}:</Paragraph>
          <Paragraph className="text-right text-muted">#{props.order.identifier}</Paragraph>
        </div>
        <div className="grid w-full grid-cols-2 gap-6">
          <Paragraph>{t('text393')}:</Paragraph>
          <Paragraph className="text-right text-muted">
            {new Date(props.order.created_at).toLocaleString(locale)}
          </Paragraph>
        </div>
        <div className="grid w-full grid-cols-2 gap-6">
          <Paragraph>{t('text26')}:</Paragraph>
          <Paragraph className="text-right text-muted">
            {props.order.price_with_vat?.toFixed(2) || props.order.price.toFixed(2)} {currency}
          </Paragraph>
        </div>
        <div className="grid w-full grid-cols-2 gap-6">
          <Paragraph>{t('text245')}:</Paragraph>
          <div className="col-span-1 flex w-full items-center justify-end">
            <ProfileOrderStatus order={props.order} />
          </div>
        </div>
      </div>
      {!props.order.documents.length && <Paragraph>{t('text805')}</Paragraph>}
      {props.order.documents.slice(1, 2).map((document, index) => (
        <Button
          disabled={!isPaid}
          key={index}
          onClick={() => handleDownload(document)}
          className="w-full"
        >
          <IoDocumentTextOutline size={24} /> {t('text116')}
        </Button>
      ))}
      <ProfileRepeatOrderButton orderId={props.order.id} className="w-full" />

      <OrderLoyaltyReward reward={props.order.loyalty_reward} refreshOrders />
    </div>
  );
}
